import { FileReference } from '@kaizenplatform/belfast-entity';
import { FileIdsValue, SelectMultipleValue } from '@kaizenplatform/hugot-entity';
import { ClassAttributes, createElement } from 'react';

import {
  DynamicSchemaDefinitionChoice,
  DynamicSchemaDefinitionFieldType,
  DynamicSchemaDefinitionFiles,
  DynamicSchemaDefinitionFixed,
  DynamicSchemaDefinitionMultiLineText,
  DynamicSchemaDefinitionPositiveInteger,
  DynamicSchemaDefinitionSelectMultiple,
  DynamicSchemaDefinitionSelectOne,
  DynamicSchemaDefinitionSingleLineText,
  DynamicSchemaDefinitionToggle,
} from '../../../../../graphql/generated/graphql';
import AsFiles from './AsFiles';
import AsMultipleSelection from './AsMultipleSelection';
import AsPlainText from './AsPlainText';
import AsSelection from './AsSelection';
import AsToggle from './AsToggle';

interface Props {
  field:
    | Pick<DynamicSchemaDefinitionSingleLineText, 'type' | 'label'>
    | Pick<DynamicSchemaDefinitionMultiLineText, 'type' | 'label'>
    | Pick<DynamicSchemaDefinitionPositiveInteger, 'type' | 'label'>
    | Pick<DynamicSchemaDefinitionFixed, 'type' | 'label' | 'value'>
    | Pick<DynamicSchemaDefinitionFiles, 'type' | 'label'>
    | (Pick<DynamicSchemaDefinitionSelectOne, 'type' | 'label'> & {
        choices: Array<Pick<DynamicSchemaDefinitionChoice, 'key' | 'label'>>;
      })
    | (Pick<DynamicSchemaDefinitionSelectMultiple, 'type' | 'label'> & {
        choices: Array<Pick<DynamicSchemaDefinitionChoice, 'key' | 'label'>>;
      })
    | Pick<DynamicSchemaDefinitionToggle, 'type' | 'label' | 'checkedControlLabel' | 'uncheckedControlLabel'>;
  value: string | number | boolean | SelectMultipleValue | FileIdsValue | null | undefined;
  files?: FileReference[];
  latestValue: string | number | boolean | SelectMultipleValue | FileIdsValue | null | undefined;
  beforeValue: string | number | boolean | SelectMultipleValue | FileIdsValue | null | undefined;
  latestFiles?: FileReference[];
  beforeFiles?: FileReference[];
  className?: string;
}

const Field = ({
  field,
  value,
  files,
  latestValue,
  beforeValue,
  latestFiles,
  beforeFiles,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  if (
    field.type == DynamicSchemaDefinitionFieldType.SingleLineText ||
    field.type == DynamicSchemaDefinitionFieldType.MultiLineText ||
    field.type == DynamicSchemaDefinitionFieldType.PositiveInteger
  ) {
    return (
      <AsPlainText
        field={field}
        value={value as string | number | null}
        latestValue={latestValue as string | number | null}
        beforeValue={beforeValue as string | number | null}
        className={className}
      />
    );
  }

  if (field.type == DynamicSchemaDefinitionFieldType.Fixed) {
    const plainField = field as Pick<DynamicSchemaDefinitionFixed, 'label' | 'value'>;
    return (
      <AsPlainText
        field={plainField}
        value={plainField.value}
        latestValue={latestValue as string | number | null}
        beforeValue={beforeValue as string | number | null}
        className={className}
      />
    );
  }

  if (field.type == DynamicSchemaDefinitionFieldType.SelectOne) {
    const selectOneField = field as Pick<DynamicSchemaDefinitionSelectOne, 'label'> & {
      choices: Array<Pick<DynamicSchemaDefinitionChoice, 'key' | 'label'>>;
    };
    return (
      <AsSelection
        field={selectOneField}
        value={value as string}
        latestValue={latestValue as string}
        beforeValue={beforeValue as string}
        className={className}
      />
    );
  }

  if (field.type == DynamicSchemaDefinitionFieldType.SelectMultiple) {
    const selectMultipleField = field as Pick<DynamicSchemaDefinitionSelectMultiple, 'label'> & {
      choices: Array<Pick<DynamicSchemaDefinitionChoice, 'key' | 'label'>>;
    };
    return (
      <AsMultipleSelection
        field={selectMultipleField}
        value={value as SelectMultipleValue}
        latestValue={latestValue as SelectMultipleValue}
        beforeValue={beforeValue as SelectMultipleValue}
        className={className}
      />
    );
  }

  if (field.type == DynamicSchemaDefinitionFieldType.Toggle) {
    const toggleField = field as Pick<
      DynamicSchemaDefinitionToggle,
      'label' | 'checkedControlLabel' | 'uncheckedControlLabel'
    >;
    return (
      <AsToggle
        field={toggleField}
        value={value as boolean}
        latestValue={latestValue as boolean}
        beforeValue={beforeValue as boolean}
        className={className}
      />
    );
  }

  if (field.type == DynamicSchemaDefinitionFieldType.Files) {
    const filesField = field as Pick<DynamicSchemaDefinitionFiles, 'label'>;
    return (
      <AsFiles
        field={filesField}
        bucketId={value as string}
        beforeBucketId={beforeValue as string}
        latestBucketId={latestValue as string}
        files={files as FileReference[]}
        beforeFiles={beforeFiles as FileReference[]}
        latestFiles={latestFiles as FileReference[]}
        className={className}
      />
    );
  }

  return null;
};

export default Field;
